import Vue from "vue";
import Router, { RouteConfig } from "vue-router";
import Util from "@/lib/util";
import NProgress from "nprogress";
import "nprogress/nprogress.css";

/* Layout */
import Layout from "@/layout/index.vue";
import managingSchoolsRouter from "./modules/managin-schools";
/* Router modules */
import settingsRouter from "./modules/settings";
import multiTenancyRouter from "./modules/multi-tenancy";
import paymentRouter from "./modules/payment";
import paySettingsRouter from "./modules/paysettings";
import scoreSettingsRouter from "./modules/score-settings";
import pushSettingsRouter from "./modules/push-settings";
import faceSettingRouter from "./modules/face-settings";
import shoppingRouter from "./modules/shopping";
import pointProductRouter from "./modules/points-product"
import cusServiceSysRouter from "./modules/cus-serviceSys"

Vue.use(Router);

/*
  Note: sub-menu only appear when children.length>=1
  Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
*/

/*
  name:'router-name'             the name field is required when using <keep-alive>, it should also match its component's name property
                                 detail see : https://vuejs.org/v2/guide/components-dynamic-async.html#keep-alive-with-Dynamic-Components
  redirect:                      if set to 'noredirect', no redirect action will be trigger when clicking the breadcrumb
  meta: {
    roles: ['admin', 'editor']   will control the page roles (allow setting multiple roles)
    title: 'title'               the name showed in subMenu and breadcrumb (recommend set)
    icon: 'svg-name'             the icon showed in the sidebar
    hidden: true                 if true, this route will not show in the sidebar (default is false)
    alwaysShow: true             if true, will always show the root menu (default is false)
                                 if false, hide the root menu when has less or equal than one children route
    breadcrumb: false            if false, the item will be hidden in breadcrumb (default is true)
    noCache: true                if true, the page will not be cached (default is false)
    affix: true                  if true, the tag will affix in the tags-view
    activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
  }
*/

/**
  ConstantRoutes
  a base page that does not have permission requirements
  all roles can be accessed
*/
export const constantRoutes: RouteConfig[] = [
  {
    path: "/account",
    name: "Account",
    redirect: "/account/login",
    component: () => import("@/views/account/index.vue"),
    meta: { hidden: true },
    children: [
      {
        path: "login",
        name: "login",
        component: () =>
          import(/* webpackChunkName: "login" */ "@/views/account/login/index.vue"),
        meta: { hidden: true, title: "登录跳转中..." }
      },
      {
        path: "redirect",
        component: () =>
          import(/* webpackChunkName: "redirect" */ "@/views/account/redirect/index.vue"),
        name: "redirect",
        meta: {
          hidden: true,
          title: "登录成功，跳转中...",
          requirePermission: false
        }
      },
      {
        path: "logout",
        component: () =>
          import(/* webpackChunkName: "redirect" */ "@/views/account/logout/index.vue"),
        name: "logout",
        meta: { hidden: true, title: "已退出登录" }
      }
    ]
  },

  {
    path: "/404",
    component: () =>
      import(/* webpackChunkName: "404" */ "@/views/error-page/404.vue"),
    meta: { hidden: true, title: "页面未找到" }
  },
  {
    path: "/401",
    component: () =>
      import(/* webpackChunkName: "401" */ "@/views/error-page/401.vue"),
    meta: { hidden: true, title: "没有权限" }
  },
  {
    path: "/",
    component: Layout,
    redirect: "/dashboard",
    children: [
      {
        path: "dashboard",
        component: () =>
          import(/* webpackChunkName: "dashboard" */ "@/views/dashboard/index.vue"),
        name: "dashboard",
        meta: {
          title: "主页",
          icon: "dashboard",
          affix: true
        }
      }
    ]
  }
  // {
  //   path: '/documentation',
  //   component: Layout,
  //   children: [
  //     {
  //       path: 'index',
  //       component: () => import(/* webpackChunkName: "documentation" */ '@/views/documentation/index.vue'),
  //       name: 'Documentation',
  //       meta: { title: 'documentation', icon: 'documentation', affix: true }
  //     }
  //   ]
  // },
];

/**
 * asyncRoutes
 * the routes that need to be dynamically loaded based on user roles
 */
export const asyncRoutes: RouteConfig[] = [
  ...constantRoutes,
  managingSchoolsRouter,
  multiTenancyRouter,
  paymentRouter,
  settingsRouter,
  paySettingsRouter,
  scoreSettingsRouter,
  pushSettingsRouter,
  faceSettingRouter,
  shoppingRouter,
  pointProductRouter,
  cusServiceSysRouter
];

const createRouter = () =>
  new Router({
    // mode: "history", // Disabled due to Github Pages doesn't support this, enable this if you need.
    scrollBehavior: (to, from, savedPosition) => {
      if (savedPosition) {
        return savedPosition;
      } else {
        return { x: 0, y: 0 };
      }
    },
    base: process.env.BASE_URL,
    routes: asyncRoutes
  });

const router = createRouter();

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter() {
  const newRouter = createRouter();
  (router as any).matcher = (newRouter as any).matcher; // reset router
}

export default router;

NProgress.configure({ showSpinner: false });

const whiteList = ["login", "redirect"];

router.beforeEach(async (to, _, next) => {
  // Start progress bar
  NProgress.start();

  if (
    !Util.abp.session.userId &&
    (to.name !== "login" && to.name !== "redirect" && to.name !== "logout")
  ) {
    next({
      name: "login"
    });
  } else if (
    !!Util.abp.session.userId &&
    (to.name === "login" || to.name === "redirect")
  ) {
    next({
      name: "dashboard"
    });
  } else {
    const curRouterObj = Util.getRouterObjByName([...asyncRoutes], to.name);
    if (curRouterObj && curRouterObj.meta && curRouterObj.meta.permission) {
      if (window.abp.auth.hasPermission(curRouterObj.meta.permission)) {
        Util.toDefaultPage([...asyncRoutes], to.name, router, next);
      } else {
        next({
          path: "/401"
        });
      }
    } else {
      Util.toDefaultPage([...asyncRoutes], to.name, router, next);
    }
  }

  NProgress.done();
  // Determine whether the user has logged in
});

router.afterEach(to => {
  // Finish progress bar
  NProgress.done();

  // set page title
  document.title = to.meta.title;
});
